import { createContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import io from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";

import {
  NOTIFICATION_SOCKET_URL,
  SOCKET_RETRY_ATTEMPTS,
  API_URL,
} from "../app/common/constants";
import { NotificationListeners } from "../app/notifications/duck/listener";
import axios from "../app/common/axios"; // Use configured axios instance
import CustomChatNotification from "../app/notifications/components/CustomChatNotification";

const NotificationWsContext = createContext(null);
export { NotificationWsContext };

const NotificationWebSocket = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [retries, setRetries] = useState(0);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [reconnectionTrigger, setReconnectionTrigger] = useState(0);
  const gameplayerId = useSelector((state) => state.user?.profile?._id);
  const isAuthenticated = useSelector((state) => state.user?.isAuthenticated);
  const dispatch = useDispatch();
  const previousAccessToken = useRef(null);
  const maxRetries = 3;

  // Create stable handlers with useCallback to prevent stale closures
  const handleLoginConfirmed = useCallback(
    (gameplayerId) => {
      if (socket) {
        socket.emit(
          "roomJoin",
          `nbyula-discovery-${gameplayerId}`,
          (err, _data) => {
            if (err) {
              console.error("Failed to join notification room:", err);
              return;
            }
          }
        );
      }
    },
    [socket]
  );

  const handleLoginRejected = useCallback(() => {
    console.info(
      "NotificationSocket login rejected - attempting token renewal"
    );

    setRetries((currentRetries) => {
      if (currentRetries >= maxRetries) {
        console.error(
          "NotificationSocket auth failed after",
          maxRetries,
          "retries"
        );
        toast.error(
          "Failed to connect to notification service. Please refresh the page.",
          { autoClose: false }
        );
        // Don't force logout here - let the main axios interceptor handle it
        return currentRetries;
      }

      // Check if user is still authenticated before making API call
      if (!isAuthenticated || !gameplayerId) {
        console.info("User no longer authenticated, skipping renewal");
        return currentRetries;
      }

      // Let axios interceptor handle the renewal automatically
      // Make a request to presence API to trigger renewal if needed
      axios
        .post(`${API_URL}/v1/presence`, {
          entity_id: gameplayerId,
        })
        .then((_res) => {
          console.info("Token renewed successfully for NotificationSocket");
          // Force socket reconnection by triggering state change
          setSocket(null);
          setReconnectionTrigger((prev) => prev + 1);
        })
        .catch((err) => {
          console.error("Token renewal failed for NotificationSocket:", err);
          // If presence API fails due to network issues, try again
          if (err.code === "NETWORK_ERROR" || err.response?.status >= 500) {
            console.info("Network error, will retry on next login rejection");
          }
          // axios interceptor will handle logout and reload for auth errors
        });

      return currentRetries + 1;
    });
  }, [gameplayerId, isAuthenticated, maxRetries]);

  // Track access token changes to detect renewal
  useEffect(() => {
    const checkTokenChange = () => {
      const currentToken = localStorage.getItem("accessToken");
      if (currentToken && currentToken !== previousAccessToken.current) {
        previousAccessToken.current = currentToken;

        // If socket exists and token changed, force reconnection
        if (socket && isAuthenticated) {
          console.info(
            "NotificationSocket: Access token updated, forcing reconnection"
          );
          setSocket(null);
          setReconnectionTrigger((prev) => prev + 1);
        }
      }
    };

    // Check immediately
    checkTokenChange();

    // Set up interval to check for token changes
    const intervalId = setInterval(checkTokenChange, 1000); // Check every second

    return () => clearInterval(intervalId);
  }, [socket, isAuthenticated]);

  useEffect(() => {
    if (socket) {
      socket.on("loginConfirmed", handleLoginConfirmed);
      socket.on("loginRejected", handleLoginRejected);

      socket.on("notificationReceived", (message) => {
        // Update Redux state
        dispatch(NotificationListeners.notificationReceived(message));

        // Trigger notification list reload for real-time updates
        dispatch(NotificationListeners.loadNewNotifications(true));

        // Show toast notification
        const title = message.title || message.subject || "New Notification";
        const body = message.body;
        const clickAction = message.clickAction || "/";
        const icon = message.icon;

        // Show toast
        toast(
          <CustomChatNotification
            title={title}
            body={body}
            link={clickAction}
            avatarSrc={icon}
          />,
          {
            toastId: clickAction,
          }
        );
      });

      socket.on("disconnect", () => {
        console.info("NotificationSocket disconnected");
      });

      socket.on("connect", () => {
        setRetries(0); // Reset retries on successful connection
      });

      socket.on("error", (error) => {
        console.error("NotificationSocket error occurred:", error);
      });

      socket.on("reconnect_failed", () => {
        console.error("NotificationSocket reconnection failed");
      });

      // Cleanup function for socket events
      return () => {
        socket.off("loginConfirmed", handleLoginConfirmed);
        socket.off("loginRejected", handleLoginRejected);
        socket.off("notificationReceived");
        socket.off("disconnect");
        socket.off("connect");
        socket.off("error");
        socket.off("reconnect_failed");
      };
    } else {
      // Get token from localStorage
      const currentAccessToken = localStorage.getItem("accessToken");

      // Only create socket if authenticated with valid tokens and not already disconnecting
      if (
        gameplayerId &&
        currentAccessToken &&
        isAuthenticated &&
        !isDisconnecting
      ) {
        let query = `gameplayerId=${gameplayerId}&token=${currentAccessToken}`;
        let opts = {
          query,
          reconnect: true,
          transports: ["websocket"],
          path: import.meta.env.PROD ? "/unified-notification-service" : "",
          forceNew: true,
          reconnectionAttempts: SOCKET_RETRY_ATTEMPTS,
          timeout: 2000,
        };
        setSocket(io.connect(NOTIFICATION_SOCKET_URL, opts));
      }
    }
  }, [
    socket,
    gameplayerId,
    isAuthenticated,
    retries,
    dispatch,
    isDisconnecting,
    reconnectionTrigger,
    handleLoginConfirmed,
    handleLoginRejected,
  ]);

  // Handle logout cleanup
  useEffect(() => {
    if (!isAuthenticated && socket) {
      console.info("User logged out, cleaning up NotificationSocket");
      setIsDisconnecting(true);

      // Clean disconnect
      socket.disconnect();
      setSocket(null);
      setRetries(0);

      // Reset disconnecting state after cleanup
      setTimeout(() => setIsDisconnecting(false), 100);
    }
  }, [isAuthenticated, socket]);

  return (
    <NotificationWsContext.Provider value={{ socket }}>
      {children}
    </NotificationWsContext.Provider>
  );
};

export default NotificationWebSocket;
